import React, { useState, useEffect, useRef } from "react";
import { createTheme, ThemeProvider, makeStyles } from "@material-ui/core/styles";
import { amber } from "@material-ui/core/colors";
import {
  TextField,
  Typography,
  Grid,
  Button,
  Paper,
  Divider,
  CircularProgress,
  Collapse,
  IconButton,
  InputAdornment,
} from "@material-ui/core";
import FingerprintIcon from "@material-ui/icons/Fingerprint";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import SmsIcon from "@material-ui/icons/Sms";
import EmailIcon from "@material-ui/icons/Email";
import VpnKeyIcon from "@material-ui/icons/VpnKey";
import logo from "../../../img/geetee.png";
import { firestore, functions, auth } from "../../../firebase/firebase";
import { useAuth } from "../../../providers/AuthContext";
import { signInWithCustomToken } from "firebase/auth";

const customTheme = createTheme({
  overrides: {
    MuiOutlinedInput: {
      root: {
        "&$focused $notchedOutline": {
          borderColor: amber[500],
        },
        "&:hover $notchedOutline": {
          borderColor: amber[500],
        },
      },
      notchedOutline: {
        borderColor: "rgba(255, 255, 255, 0.3)",
      },
      input: {
        color: "white",
      },
    },
    MuiFormLabel: {
      root: {
        color: "rgba(255, 255, 255, 0.7)",
        "&$focused": {
          color: amber[500],
        },
      },
    },
    MuiButton: {
      contained: {
        backgroundColor: amber[600],
        color: "#1a1a1a",
        "&:hover": {
          backgroundColor: amber[700],
        },
        "&:disabled": {
          backgroundColor: "rgba(255, 193, 7, 0.3)",
          color: "rgba(255, 255, 255, 0.5)",
        },
      },
      outlined: {
        borderColor: "rgba(255, 255, 255, 0.3)",
        color: "white",
        "&:hover": {
          borderColor: amber[500],
          backgroundColor: "rgba(255, 193, 7, 0.1)",
        },
      },
      text: {
        color: amber[500],
        "&:hover": {
          backgroundColor: "rgba(255, 193, 7, 0.1)",
        },
      },
    },
  },
});

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100vh",
    padding: theme.spacing(2),
    backgroundColor: "#1a1a1a",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(3),
    },
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(2, 1.5),
    },
  },
  logo: {
    maxWidth: "220px",
    marginBottom: theme.spacing(4),
    width: "100%",
    height: "auto",
    [theme.breakpoints.down("sm")]: {
      maxWidth: "200px",
      marginBottom: theme.spacing(3.5),
    },
    [theme.breakpoints.down("xs")]: {
      maxWidth: "180px",
      marginBottom: theme.spacing(3),
    },
  },
  paper: {
    backgroundColor: "rgba(30, 30, 30, 0.95)",
    padding: theme.spacing(5),
    borderRadius: 16,
    width: "100%",
    maxWidth: 450,
    border: "1px solid rgba(255, 193, 7, 0.2)",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.4)",
    [theme.breakpoints.down("sm")]: {
      maxWidth: 420,
      padding: theme.spacing(4),
      borderRadius: 12,
    },
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(4, 3),
      borderRadius: 12,
      maxWidth: "calc(100% - 32px)",
      backgroundColor: "rgba(30, 30, 30, 0.98)",
      border: "1px solid rgba(255, 193, 7, 0.2)",
    },
  },
  title: {
    color: "white",
    fontWeight: 500,
    marginBottom: theme.spacing(1.5),
    textAlign: "center",
    [theme.breakpoints.down("sm")]: {
      marginBottom: theme.spacing(1.25),
    },
    [theme.breakpoints.down("xs")]: {
      marginBottom: theme.spacing(1),
    },
  },
  subtitle: {
    color: "rgba(255, 255, 255, 0.6)",
    marginBottom: theme.spacing(4),
    textAlign: "center",
    [theme.breakpoints.down("sm")]: {
      marginBottom: theme.spacing(3.5),
    },
    [theme.breakpoints.down("xs")]: {
      marginBottom: theme.spacing(3),
    },
  },
  form: {
    width: "100%",
  },
  textField: {
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
    [theme.breakpoints.down("xs")]: {
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
      "& .MuiOutlinedInput-input": {
        fontSize: "16px", // Prevents zoom on iOS
      },
      "& .MuiInputLabel-outlined": {
        fontSize: "16px", // Prevents zoom on iOS
      },
    },
  },
  submitButton: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(1.5),
    fontWeight: 600,
    minHeight: 48,
    [theme.breakpoints.down("sm")]: {
      marginTop: theme.spacing(2.5),
    },
    [theme.breakpoints.down("xs")]: {
      marginTop: theme.spacing(2),
      minHeight: 44,
    },
  },
  dividerContainer: {
    display: "flex",
    alignItems: "center",
    margin: theme.spacing(3, 0),
    [theme.breakpoints.down("sm")]: {
      margin: theme.spacing(2.5, 0),
    },
    [theme.breakpoints.down("xs")]: {
      margin: theme.spacing(2, 0),
    },
  },
  divider: {
    flex: 1,
    backgroundColor: "rgba(255, 255, 255, 0.2)",
  },
  dividerText: {
    color: "rgba(255, 255, 255, 0.5)",
    padding: theme.spacing(0, 2),
    fontSize: "0.875rem",
    [theme.breakpoints.down("xs")]: {
      fontSize: "0.75rem",
      padding: theme.spacing(0, 1.5),
    },
  },
  passkeyButton: {
    padding: theme.spacing(1.5),
    borderColor: "rgba(255, 255, 255, 0.3)",
    minHeight: 48,
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1.4),
      fontSize: "0.9rem",
    },
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(1.25),
      fontSize: "0.875rem",
      minHeight: 44,
      "& .MuiButton-startIcon": {
        marginRight: theme.spacing(0.75),
      },
    },
  },
  errorMsg: {
    backgroundColor: "rgba(244, 67, 54, 0.1)",
    border: "1px solid rgba(244, 67, 54, 0.3)",
    borderRadius: 8,
    padding: theme.spacing(1.5),
    marginTop: theme.spacing(2),
    textAlign: "center",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1.25),
      marginTop: theme.spacing(1.75),
      borderRadius: 7,
    },
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(1),
      marginTop: theme.spacing(1.5),
      borderRadius: 6,
    },
  },
  errorText: {
    color: "#f44336",
    fontSize: "0.875rem",
    lineHeight: 1.4,
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.8125rem",
    },
    [theme.breakpoints.down("xs")]: {
      fontSize: "0.8rem",
    },
  },
  mfaHeader: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      marginBottom: theme.spacing(1.75),
    },
    [theme.breakpoints.down("xs")]: {
      marginBottom: theme.spacing(1.5),
    },
  },
  mfaTitle: {
    color: "white",
    [theme.breakpoints.down("sm")]: {
      fontSize: "1.15rem",
    },
    [theme.breakpoints.down("xs")]: {
      fontSize: "1.1rem",
    },
  },
  backButton: {
    color: "rgba(255, 255, 255, 0.7)",
    marginRight: theme.spacing(1),
    padding: theme.spacing(0.5),
    [theme.breakpoints.down("sm")]: {
      marginRight: theme.spacing(0.75),
      padding: theme.spacing(0.375),
    },
    [theme.breakpoints.down("xs")]: {
      marginRight: theme.spacing(0.5),
      padding: theme.spacing(0.25),
    },
  },
  mfaInfo: {
    color: "rgba(255, 255, 255, 0.6)",
    fontSize: "0.875rem",
    marginBottom: theme.spacing(2),
    textAlign: "center",
    lineHeight: 1.5,
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.8125rem",
      marginBottom: theme.spacing(1.75),
    },
    [theme.breakpoints.down("xs")]: {
      fontSize: "0.8rem",
      marginBottom: theme.spacing(1.5),
      lineHeight: 1.4,
    },
  },
  resendButton: {
    marginTop: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.85rem",
      marginTop: theme.spacing(0.75),
    },
    [theme.breakpoints.down("xs")]: {
      fontSize: "0.8rem",
      marginTop: theme.spacing(0.5),
    },
  },
  codeInput: {
    "& input": {
      textAlign: "center",
      fontSize: "1.25rem",
      letterSpacing: "0.5em",
      fontWeight: 500,
    },
    [theme.breakpoints.down("sm")]: {
      "& input": {
        fontSize: "1.15rem",
        letterSpacing: "0.45em",
      },
    },
    [theme.breakpoints.down("xs")]: {
      "& input": {
        fontSize: "1.1rem",
        letterSpacing: "0.4em",
        padding: "14px",
      },
    },
  },
}));

const SignIn = (props) => {
  const classes = useStyles();
  const isMountedRef = useRef(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [requiresTwoFactor, setRequiresTwoFactor] = useState(false);
  const [totp, setTotp] = useState(null);
  const [totps, setTotps] = useState("");
  const [usedRecoveryCode, setUsedRecoveryCode] = useState(false);
  const [type, setType] = useState("None");
  const [docId, setDocId] = useState("");
  const [passkeySupported, setPasskeySupported] = useState(false);
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const [passkeyMethod, setPasskeyMethod] = useState("MobileAuth");

  const { login } = useAuth();

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const validateCode = functions.httpsCallable("validateTotpCode");
  const sendCode = functions.httpsCallable("sendMFA");
  const sendMemo = functions.httpsCallable("sendOutMemo");
  const generatePasskeyAuthenticationOptions = functions.httpsCallable(
    "generatePasskeyAuthenticationOptions"
  );
  const verifyPasskeyAuthentication = functions.httpsCallable(
    "verifyPasskeyAuthentication"
  );

  const MINUTES = 15;

  useEffect(() => {
    if (error) {
      const timeout = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timeout);
    }
  }, [error]);

  useEffect(() => {
    if (window.PublicKeyCredential) {
      window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable().then(
        (available) => {
          if (isMountedRef.current) {
            setPasskeySupported(available);
          }
        }
      );
    }
  }, []);

  const fetchUserData = async (email) => {
    const userQuery = await firestore
      .collection("users")
      .where("email", "==", email)
      .get();

    if (userQuery.empty) {
      setError("User not found");
      return null;
    }

    const userDoc = userQuery.docs[0];
    const user = { ...userDoc.data(), docId: userDoc.id };

    setType(user?.mfaType || "SMS");
    setTotp(user?.totpObj);
    setTotps(user?.totp?.auth_url);
    setDocId(userDoc.id);
    setPhone(user.phone || "");

    return user;
  };

  const generateMfaCode = async (docId, phone, email, type) => {
    const mfaCode = Math.floor(100000 + Math.random() * 900000).toString();
    await firestore
      .collection("users")
      .doc(docId)
      .update({
        mfaCode: mfaCode,
        mfaCodeExpires: new Date(Date.now() + MINUTES * 60000),
      });

    if (type === "SMS") await sendSMS(mfaCode, phone);
    if (type === "Email") await sendEmail(email, mfaCode);
  };

  const handleMFA = async (user, effectiveCode) => {
    if (user.mfaEnabled) {
      if (!requiresTwoFactor) {
        setRequiresTwoFactor(true);
        if (
          user.mfaType === "Email" ||
          user.mfaType === "SMS" ||
          user.mfaType === "GeeTeeApp"
        ) {
          await generateMfaCode(user.docId, user.phone, user.email, user.mfaType);
        }
        return false;
      }

      if (
        user.mfaType === "Email" ||
        user.mfaType === "SMS" ||
        user.mfaType === "GeeTeeApp"
      ) {
        const isValid = await verifyEmailMFA(docId, effectiveCode);
        if (!isValid) return false;
      } else if (user.mfaType === "MobileAuth" && !usedRecoveryCode) {
        const isValid = await verifyMobileAuthMFA(totp, effectiveCode, totps);
        if (!isValid) return false;
      } else if (user.mfaType === "MobileAuth" && usedRecoveryCode) {
        return true;
      } else if (user.mfaType === "Passkey") {
        if (passkeyMethod === "SMS" || passkeyMethod === "Email") {
          const isValid = await verifyEmailMFA(docId, effectiveCode);
          if (!isValid) return false;
        } else if (usedRecoveryCode) {
          return true;
        } else {
          const isValid = await verifyMobileAuthMFA(totp, effectiveCode, totps);
          if (!isValid) return false;
        }
      }
    }

    return true;
  };

  const verifyEmailMFA = async (docId, code) => {
    const userData = await firestore.collection("users").doc(docId).get();
    const storedCode = userData.data().mfaCode;
    const expiration = userData.data().mfaCodeExpires?.toDate();

    if (!storedCode || new Date() > expiration || code !== storedCode) {
      setError("Invalid or expired code");
      setCode("");
      return false;
    }

    await firestore.collection("users").doc(docId).update({
      mfaCode: null,
      mfaCodeExpires: null,
    });

    setError("");
    return true;
  };

  const handleSignIn = async (event, codeOverride) => {
    event?.preventDefault?.();
    setError("");
    setLoading(true);

    // Auto-submit and Enter-key paths fire before React commits the latest
    // `code` state, so the closure can capture a value missing the last digit.
    // Callers that have the freshest code pass it as `codeOverride`.
    const effectiveCode = codeOverride !== undefined ? codeOverride : code;

    try {
      const user = await fetchUserData(email.toLowerCase());
      if (!user) {
        setLoading(false);
        return;
      }

      const mfaPassed = await handleMFA(user, effectiveCode);
      if (!mfaPassed) {
        setLoading(false);
        return;
      }

      await authenticateUser(email, password);
    } catch (error) {
      console.error(error);
      setError(error.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const base64urlToBuffer = (base64url) => {
    const base64 = base64url.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      "="
    );
    const binary = atob(padded);
    const buffer = new ArrayBuffer(binary.length);
    const bytes = new Uint8Array(buffer);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return buffer;
  };

  const bufferToBase64url = (buffer) => {
    const bytes = new Uint8Array(buffer);
    let binary = "";
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary)
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=/g, "");
  };

  const authenticateWithPasskey = async () => {
    if (!passkeySupported) {
      setError("Passkeys are not supported on this device");
      return;
    }

    setPasskeyLoading(true);
    setError("");

    try {
      const { data: options } = await generatePasskeyAuthenticationOptions();

      const publicKeyOptions = {
        ...options,
        challenge: base64urlToBuffer(options.challenge),
        allowCredentials: options.allowCredentials?.map((cred) => ({
          ...cred,
          id: base64urlToBuffer(cred.id),
        })),
      };

      const credential = await navigator.credentials.get({
        publicKey: publicKeyOptions,
      });

      const { data: result } = await verifyPasskeyAuthentication({
        challengeId: options.challengeId,
        credential: {
          id: credential.id,
          rawId: bufferToBase64url(credential.rawId),
          response: {
            authenticatorData: bufferToBase64url(
              credential.response.authenticatorData
            ),
            clientDataJSON: bufferToBase64url(
              credential.response.clientDataJSON
            ),
            signature: bufferToBase64url(credential.response.signature),
            userHandle: credential.response.userHandle
              ? bufferToBase64url(credential.response.userHandle)
              : null,
          },
          type: credential.type,
        },
      });

      if (result.verified && result.customToken) {
        await signInWithCustomToken(auth, result.customToken);
      } else {
        setError("Passkey authentication failed");
      }
    } catch (error) {
      console.error("Passkey authentication failed:", error);
      if (error.name !== "NotAllowedError") {
        setError("Passkey authentication failed");
      }
    } finally {
      setPasskeyLoading(false);
    }
  };

  const verifyMobileAuthMFA = async (totp, code, totps) => {
    setLoading(true);
    try {
      const result = await validateCode({ totp: totp, code: code, url: totps });
      setLoading(false);

      if (!result.data.success) {
        setError("Invalid authenticator code");
        setCode("");
        return false;
      }

      setCode("");
      setError("");
      return true;
    } catch (error) {
      console.error(error);
      setLoading(false);
      setError("Invalid authenticator code");
      setCode("");
      return false;
    }
  };

  const verifyMobileAuthRecovery = async (code) => {
    setLoading(true);
    try {
      const userDoc = await firestore.collection("users").doc(docId).get();
      const user = userDoc.data();
      const recoveryCodes = user.totp.recoveryCodes;

      if (!recoveryCodes.includes(code)) {
        setError("Invalid recovery code");
        setLoading(false);
        setCode("");
        return false;
      }

      const index = recoveryCodes.indexOf(code);
      recoveryCodes.splice(index, 1);
      await firestore.collection("users").doc(docId).update({
        "totp.recoveryCodes": recoveryCodes,
      });

      setLoading(false);
      setCode("");
      setError("");
      setUsedRecoveryCode(true);
      return true;
    } catch (error) {
      console.error(error);
      setLoading(false);
      setError("Invalid recovery code");
      setCode("");
      return false;
    }
  };

  const authenticateUser = async (email, password) => {
    const loginResult = await login(email, password).catch((err) => {
      setError("Invalid email or password");
      return null;
    });

    if (!loginResult) return false;
    return true;
  };

  const sendSMS = async (code, phoneNumber) => {
    try {
      await sendCode({
        sandbox: process.env.NODE_ENV === "development",
        phone: phoneNumber
          .replace(/\s/g, "")
          .replace(/-/g, "")
          .replace(/\(/g, "")
          .replace(/\)/g, ""),
        code: code,
      });
    } catch (error) {
      console.error("SMS sending error:", error);
      throw new Error("Failed to send verification code");
    }
  };

  const sendEmail = async (email, code) => {
    try {
      await sendMemo({
        sandbox: process.env.NODE_ENV === "development",
        email: email,
        subject: "Your Sign-In Code",
        html: `Your sign-in code is: <p style="font-size:30px"><b>${code}</b></p>
        <br/><p>This code is valid for 15 minutes.</p>`,
        code: code,
      });
    } catch (error) {
      console.error("Email sending error:", error);
      throw new Error("Failed to send verification code");
    }
  };

  const resendCode = async () => {
    if (!docId) {
      setError("Cannot resend code");
      return;
    }
    setSending(true);
    setError(null);
    try {
      const channel = type === "Passkey" ? passkeyMethod : type;
      await generateMfaCode(docId, phone, email, channel);
      setCode("");
    } catch (error) {
      console.error("Resend error:", error);
      setError("Failed to resend code");
    } finally {
      setSending(false);
    }
  };

  const switchPasskeyMethod = async (method) => {
    if (!docId) {
      setError("Cannot switch verification method");
      return;
    }
    setError(null);
    setCode("");
    setPasskeyMethod(method);
    if (method === "MobileAuth") return;
    setSending(true);
    try {
      await generateMfaCode(docId, phone, email, method);
    } catch (error) {
      console.error("Switch method error:", error);
      setError("Failed to send verification code");
    } finally {
      setSending(false);
    }
  };

  const handleBack = () => {
    setRequiresTwoFactor(false);
    setCode("");
    setError(null);
    setUsedRecoveryCode(false);
    setPasskeyMethod("MobileAuth");
  };

  const getMfaDescription = () => {
    switch (type) {
      case "SMS":
        return `Enter the 6-digit code sent to your phone ending in ${phone.slice(-4)}`;
      case "Email":
        return `Enter the 6-digit code sent to ${email}`;
      case "MobileAuth":
        return "Enter the code from your authenticator app";
      case "GeeTeeApp":
        return "Enter the code from the Gee Tee app";
      case "Passkey":
        if (passkeyMethod === "SMS")
          return `Enter the 6-digit code sent to your phone ending in ${phone.slice(-4)}`;
        if (passkeyMethod === "Email")
          return `Enter the 6-digit code sent to ${email}`;
        return "Enter the code from your authenticator app";
      default:
        return "Enter your verification code";
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      // For the MFA input, Enter fires before onChange commits the latest
      // value to React state — pass the input's current value so the last
      // digit isn't lost.
      if (event.target?.name === "userCode") {
        const fresh = (event.target.value || "").replace(/\D/g, "").slice(0, 6);
        handleSignIn(event, fresh);
      } else {
        handleSignIn(event);
      }
    }
  };

  return (
    <div className={classes.container}>
      <img src={logo} className={classes.logo} alt="Gee Tee Holdings" />

      <Paper className={classes.paper} elevation={0}>
        <ThemeProvider theme={customTheme}>
          <Collapse in={!requiresTwoFactor}>
            {!requiresTwoFactor && (
              <>
                <Typography variant="h5" className={classes.title}>
                  Welcome Back
                </Typography>
                <Typography variant="body2" className={classes.subtitle}>
                  Sign in to access your dashboard
                </Typography>

                <form className={classes.form} onSubmit={handleSignIn}>
                  <TextField
                    type="email"
                    name="userEmail"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyPress={handleKeyPress}
                    label="Email"
                    margin="normal"
                    variant="outlined"
                    fullWidth
                    autoComplete="email"
                    autoFocus
                    className={classes.textField}
                  />

                  <TextField
                    type={showPassword ? "text" : "password"}
                    name="userPassword"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyPress={handleKeyPress}
                    label="Password"
                    margin="normal"
                    variant="outlined"
                    fullWidth
                    autoComplete="current-password"
                    className={classes.textField}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            style={{ color: "rgba(255, 255, 255, 0.5)" }}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    disabled={loading || !email || !password}
                    className={classes.submitButton}
                  >
                    {loading ? (
                      <CircularProgress size={24} style={{ color: "#1a1a1a" }} />
                    ) : (
                      "Sign In"
                    )}
                  </Button>
                </form>

                {passkeySupported && (
                  <>
                    <div className={classes.dividerContainer}>
                      <Divider className={classes.divider} />
                      <Typography className={classes.dividerText}>or</Typography>
                      <Divider className={classes.divider} />
                    </div>

                    <Button
                      variant="outlined"
                      fullWidth
                      disabled={passkeyLoading}
                      onClick={authenticateWithPasskey}
                      className={classes.passkeyButton}
                      startIcon={
                        passkeyLoading ? (
                          <CircularProgress size={20} style={{ color: "white" }} />
                        ) : (
                          <FingerprintIcon />
                        )
                      }
                    >
                      {passkeyLoading ? "Authenticating..." : "Sign in with Passkey"}
                    </Button>
                  </>
                )}
              </>
            )}
          </Collapse>

          <Collapse in={requiresTwoFactor}>
            {requiresTwoFactor && (
              <>
                <div className={classes.mfaHeader}>
                  <IconButton
                    className={classes.backButton}
                    onClick={handleBack}
                    size="small"
                  >
                    <ArrowBackIcon />
                  </IconButton>
                  <Typography variant="h6" className={classes.mfaTitle}>
                    Verification Required
                  </Typography>
                </div>

                <Typography className={classes.mfaInfo}>
                  {getMfaDescription()}
                </Typography>

                <TextField
                  name="userCode"
                  value={code}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, "").slice(0, 6);
                    setCode(val);
                    if (val.length === 6 && !usedRecoveryCode) {
                      handleSignIn(e, val);
                    }
                  }}
                  onKeyPress={handleKeyPress}
                  label="Verification Code"
                  margin="normal"
                  variant="outlined"
                  fullWidth
                  autoFocus
                  placeholder="000000"
                  className={`${classes.textField} ${classes.codeInput}`}
                  inputProps={{
                    maxLength: 6,
                    inputMode: "numeric",
                  }}
                />

                <Button
                  variant="contained"
                  fullWidth
                  disabled={loading || code.length < 6}
                  onClick={handleSignIn}
                  className={classes.submitButton}
                >
                  {loading ? (
                    <CircularProgress size={24} style={{ color: "#1a1a1a" }} />
                  ) : (
                    "Verify"
                  )}
                </Button>

                {(type === "Email" || type === "SMS") && (
                  <Grid container justifyContent="center">
                    <Button
                      variant="text"
                      disabled={sending}
                      onClick={resendCode}
                      startIcon={type === "SMS" ? <SmsIcon /> : <EmailIcon />}
                      className={classes.resendButton}
                    >
                      {sending ? "Sending..." : "Resend Code"}
                    </Button>
                  </Grid>
                )}

                {type === "Passkey" && (
                  <>
                    {(phone || email) && (
                      <Grid container spacing={1}>
                        {phone && (
                          <Grid item xs={email ? 6 : 12}>
                            <Button
                              variant="text"
                              fullWidth
                              disabled={sending || loading}
                              startIcon={<SmsIcon />}
                              onClick={() =>
                                passkeyMethod === "SMS"
                                  ? resendCode()
                                  : switchPasskeyMethod("SMS")
                              }
                              className={classes.resendButton}
                            >
                              {sending
                                ? "Sending..."
                                : passkeyMethod === "SMS"
                                ? "Resend Code"
                                : "Use SMS Code"}
                            </Button>
                          </Grid>
                        )}
                        {email && (
                          <Grid item xs={phone ? 6 : 12}>
                            <Button
                              variant="text"
                              fullWidth
                              disabled={sending || loading}
                              startIcon={<EmailIcon />}
                              onClick={() =>
                                passkeyMethod === "Email"
                                  ? resendCode()
                                  : switchPasskeyMethod("Email")
                              }
                              className={classes.resendButton}
                            >
                              {sending
                                ? "Sending..."
                                : passkeyMethod === "Email"
                                ? "Resend Code"
                                : "Use Email Code"}
                            </Button>
                          </Grid>
                        )}
                      </Grid>
                    )}
                    {passkeyMethod !== "MobileAuth" && totp && (
                      <Grid container justifyContent="center">
                        <Button
                          variant="text"
                          disabled={sending || loading}
                          startIcon={<VpnKeyIcon />}
                          onClick={() => switchPasskeyMethod("MobileAuth")}
                          className={classes.resendButton}
                        >
                          Use Authenticator Code
                        </Button>
                      </Grid>
                    )}
                  </>
                )}

                {type === "MobileAuth" && (
                  <Grid container justifyContent="center">
                    <Button
                      variant="text"
                      disabled={loading}
                      onClick={async () => {
                        const recoverySuccess = await verifyMobileAuthRecovery(code);
                        if (recoverySuccess) {
                          await authenticateUser(email, password);
                        }
                      }}
                      className={classes.resendButton}
                    >
                      Use Recovery Code Instead
                    </Button>
                  </Grid>
                )}
              </>
            )}
          </Collapse>

          {error && (
            <div className={classes.errorMsg}>
              <Typography className={classes.errorText}>{error}</Typography>
            </div>
          )}
        </ThemeProvider>
      </Paper>
    </div>
  );
};

export default SignIn;
